/**
 * Shared helpers for Pokémon Hub sections (highlights JSON, analytics).
 */
(() => {
  const cache = new Map();

  const formatMoney = (cents) => {
    const fmt = window.Kl24EuroFormat;
    if (fmt) return fmt.formatFromCents(cents);
    return String(
      new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
        Math.max(0, Number(cents) || 0) / 100
      )
    ).replace(/\s*€/g, '\u00A0€');
  };

  const normalizeTags = (tags) => {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim());
    return String(tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  const parseFinishFromSku = (sku) => {
    const raw = String(sku || '').trim().toUpperCase().replace(/-EN$/, '');
    if (raw.endsWith('-RH')) return 'Reverse Holo';
    if (raw.endsWith('-H')) return 'Holo';
    return '';
  };

  const parseFinishFromTags = (tags) => {
    const lower = normalizeTags(tags).map((tag) => tag.toLowerCase());
    if (lower.some((tag) => tag.includes('reverse'))) return 'Reverse Holo';
    if (lower.some((tag) => tag.includes('holo') || tag.includes('foil') || tag === 'glitzer')) {
      return 'Holo';
    }
    return '';
  };

  const isHoloOrReverseFinish = (item) => {
    const skuFinish = parseFinishFromSku(item?.sku);
    if (skuFinish === 'Holo' || skuFinish === 'Reverse Holo') return true;
    const tagFinish = parseFinishFromTags(item?.tags);
    return tagFinish === 'Holo' || tagFinish === 'Reverse Holo';
  };

  const productToRecentSingle = (product) => {
    const variant = (product.variants || [])[0];
    if (!variant?.available) return null;
    const imageUrl = (product.images || [])[0]?.src || '';
    return {
      product_id: product.id,
      variant_id: variant.id,
      handle: product.handle,
      title: product.title,
      image_url: imageUrl,
      price_cents: Math.round(Number.parseFloat(variant.price || '0') * 100),
      url: `/products/${product.handle}`,
      published_at: product.published_at || product.created_at || '',
      sku: variant.sku || '',
      tags: normalizeTags(product.tags),
    };
  };

  const fetchHighlights = async (assetUrl) => {
    const key = assetUrl || '';
    if (!key) return null;
    if (cache.has(key)) return cache.get(key);
    try {
      const base = key.split('?')[0];
      const res = await fetch(`${base}?_cb=${Date.now()}`, { cache: 'no-cache' });
      if (!res.ok) return null;
      const data = await res.json();
      cache.set(key, data);
      return data;
    } catch {
      return null;
    }
  };

  const fetchCollectionProducts = async (collectionHandle, options = {}) => {
    const limit = Math.max(1, Number(options.limit) || 12);
    const finishFilter = options.finishFilter || 'all';
    const holoOnly = finishFilter === 'holo_reverse';
    const maxPages = holoOnly ? 12 : 6;
    const maxScanRaw = Number(options.maxScan);
    const maxScan =
      Number.isFinite(maxScanRaw) && maxScanRaw > 0
        ? maxScanRaw
        : holoOnly
          ? Number.POSITIVE_INFINITY
          : limit * 6;
    const origin = window.location.origin;
    const items = [];
    let products = [];

    const catalog = window.Kl24SinglesCatalog;
    if (catalog?.fetchSinglesProducts) {
      const result = await catalog.fetchSinglesProducts({
        collectionHandle,
        maxPages,
      });
      products = result.products || [];
    } else {
      let page = 1;
      while (page <= maxPages) {
        const url = `${origin}/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=250&page=${page}`;
        const res = await fetch(url);
        if (!res.ok) break;
        const data = await res.json();
        const batch = data.products || [];
        if (!batch.length) break;
        products.push(...batch);
        if (batch.length < 250) break;
        page += 1;
      }
    }

    let scanned = 0;
    for (const product of products) {
      scanned += 1;
      if (scanned > maxScan) break;
      const row = productToRecentSingle(product);
      if (!row) continue;
      if (holoOnly && !isHoloOrReverseFinish(row)) continue;
      items.push(row);
    }

    items.sort((a, b) => String(b.published_at).localeCompare(String(a.published_at)));
    return items.slice(0, limit);
  };

  const trackHubEvent = (name, detail = {}) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...detail });
    } catch {
      /* optional analytics */
    }
    try {
      document.dispatchEvent(new CustomEvent('kl24:hub-analytics', { detail: { name, ...detail } }));
    } catch {
      /* ignore */
    }
  };

  /** SEO alt: "[Name] [Nummer] – [Set] – Pokémon Karte" (+ finish). */
  const buildImageAlt = ({ cardName, title, cardNumber, setName, finish } = {}) => {
    const name = String(cardName || title || '').trim();
    const number = String(cardNumber || '').trim();
    const set = String(setName || '').trim();
    const variant = String(finish || '').trim();
    let head = name;
    if (number && !name.includes(number)) head = [name, number].filter(Boolean).join(' ');
    let alt = head;
    if (set) alt = alt ? `${alt} – ${set}` : set;
    alt = alt ? `${alt} – Pokémon Karte` : 'Pokémon Karte';
    if (variant) alt = `${alt} ${variant}`;
    return alt.trim();
  };

  window.Kl24PokemonHub = {
    fetchHighlights,
    fetchCollectionProducts,
    formatMoney,
    trackHubEvent,
    isHoloOrReverseFinish,
    parseFinishFromSku,
    buildImageAlt,
  };
})();
